import { useState, type FormEvent } from 'react';
import { Package, Coins, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { usePlatformOrgs, useGrantCredits, type PlatformOrg } from '@/hooks/usePlatform';
import { formatDate, formatUsd } from '@/lib/utils';

export function PlatformOrgsPage() {
  const { data, isPending } = usePlatformOrgs();
  const [granting, setGranting] = useState<PlatformOrg | null>(null);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Package className="w-5 h-5" /> Organizasyonlar
        </h1>
        <p className="text-muted-foreground text-sm">
          Tüm organizasyonların özeti. Kredi hediye et, paket durumunu izle.
        </p>
      </div>

      {isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Organizasyon</th>
                  <th className="text-left py-2 px-3 font-medium">Sahip</th>
                  <th className="text-left py-2 px-3 font-medium">Paket</th>
                  <th className="text-right py-2 px-3 font-medium">Kredi</th>
                  <th className="text-center py-2 px-3 font-medium">Üye</th>
                  <th className="text-center py-2 px-3 font-medium">Site</th>
                  <th className="text-left py-2 px-3 font-medium">Kayıt</th>
                  <th className="text-right py-2 px-3 font-medium">Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {data?.organizations.map((o) => (
                  <tr key={o.id} className="border-b last:border-b-0 hover:bg-muted/30">
                    <td className="py-2 px-3">
                      <div className="font-medium truncate">{o.name}</div>
                      <div className="text-xs text-muted-foreground truncate font-mono">{o.slug}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-xs truncate">{o.owner_name ?? o.owner_email ?? '—'}</div>
                      <div className="text-xs text-muted-foreground truncate">{o.owner_email}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="capitalize">{o.current_package_id ?? 'Yok'}</div>
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{formatUsd(o.credit_balance, 2)}</td>
                    <td className="py-2 px-3 text-center">{o.member_count}</td>
                    <td className="py-2 px-3 text-center">{o.site_count}</td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{formatDate(o.created_at)}</td>
                    <td className="py-2 px-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setGranting(o)}>
                        <Coins className="w-3.5 h-3.5" /> Bakiye
                      </Button>
                    </td>
                  </tr>
                ))}
                {data?.organizations.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                      Henüz organizasyon yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {granting && <GrantCreditsDialog org={granting} onClose={() => setGranting(null)} />}
    </div>
  );
}

function GrantCreditsDialog({ org, onClose }: { org: PlatformOrg; onClose: () => void }) {
  const grant = useGrantCredits();
  // Input is USD (decimal). We convert to micro-USD on submit.
  const [amountUsd, setAmountUsd] = useState('10.00');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const usd = Number.parseFloat(amountUsd);
    if (!Number.isFinite(usd) || usd <= 0) {
      setError('Geçerli bir USD tutarı gir');
      return;
    }
    const microUsd = Math.round(usd * 1_000_000);
    try {
      await grant.mutateAsync({ org_id: org.id, amount: microUsd, note: note || undefined });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hata');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="w-4 h-4" /> Bakiye Yükle
          </CardTitle>
          <CardDescription>
            <strong>{org.name}</strong> → mevcut: <span className="font-mono">{formatUsd(org.credit_balance, 2)}</span>
          </CardDescription>
        </CardHeader>
        <form onSubmit={onSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Tutar (USD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  id="amount"
                  type="number"
                  min={0.01}
                  step="0.01"
                  value={amountUsd}
                  onChange={(e) => setAmountUsd(e.target.value)}
                  required
                  className="pl-6"
                />
              </div>
              <div className="flex gap-1 flex-wrap">
                {[5, 10, 25, 50, 100].map((n) => (
                  <Button
                    key={n}
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAmountUsd(n.toFixed(2))}
                  >
                    ${n}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="note">Not (opsiyonel)</Label>
              <Input
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Örn: test bakiyesi"
              />
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
          </CardContent>
          <CardContent className="pt-0 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              İptal
            </Button>
            <Button type="submit" disabled={grant.isPending}>
              {grant.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              <Plus className="w-4 h-4" /> Yükle
            </Button>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
